import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BeneficFormGroupDTO, IntegrationApiResult, UserContact, UserContactsResponse } from '@Interfaces/interfaces';
import { Store } from '@ngrx/store';
import { AppState } from '@State/app.state';
import { selectContactsById } from '@State/Selectors/contacts.selectors';
import { catchError, firstValueFrom, from, map, mergeMap, Observable, of, toArray } from 'rxjs';
import { LanguageService } from './language.service';
import { APP_CONFIG } from '@Core/config/app-config';

@Injectable({
  providedIn: 'root'
})
export class ContactsService {

  private readonly config = inject(APP_CONFIG);

  constructor(
    private http:HttpClient,
    private languageService: LanguageService,
    private store: Store<AppState>
  ) { }

  public getUserContacts(): Observable<UserContactsResponse> {
    const headers: HttpHeaders = new HttpHeaders()
      .set('lang', this.languageService.getValidLanguageValue().lang_code)
      .set('module', '6')
      .set('channel', 'b2c')
      .set('token', localStorage.getItem('loginToken') ?? '');

    return this.http.get<UserContactsResponse>(
      this.config.apiUrl + '/rubrica/contacts', { headers }
    )
  }

  public createUserContact(contact: UserContact): Observable<{ result: IntegrationApiResult }> {
    const headers: HttpHeaders = new HttpHeaders()
      .set('lang', this.languageService.getValidLanguageValue().lang_code)
      .set('module', '6')
      .set('channel', 'b2c')
      .set('token', localStorage.getItem('loginToken') ?? '');


    return this.http.post<{ result: IntegrationApiResult }>(
      this.config.apiUrl + '/rubrica/contact', contact, { headers }
    )
  }

  public updateUserContact(updatedContact: UserContact): Observable<{ result: IntegrationApiResult }>{
    const headers: HttpHeaders = new HttpHeaders()
      .set('lang', this.languageService.getValidLanguageValue().lang_code)
      .set('module', '6')
      .set('channel', 'b2c')
      .set('token', localStorage.getItem('loginToken') ?? '');

    return this.http.put<{ result: IntegrationApiResult }>(
      this.config.apiUrl + `/rubrica/contact/${updatedContact.idContact}`, updatedContact, { headers }
    )
  }

  public deleteUserContact(idContact: number): Observable<{ result: IntegrationApiResult }>{
    const headers: HttpHeaders = new HttpHeaders()
      .set('lang', this.languageService.getValidLanguageValue().lang_code)
      .set('module', '6')
      .set('channel', 'b2c')
      .set('token', localStorage.getItem('loginToken') ?? '');

      return this.http.delete<{ result: IntegrationApiResult }>(
        this.config.apiUrl + `/rubrica/contact/${idContact}`, { headers }
      )
  }

  //* Utils
  public saveAndUpdateContacts(contactsToSave: UserContact[], contactsToUpdate: UserContact[]){

    //? SAVE AND UPDATE
    const allOperations = [
      ...contactsToSave.map(contact => this.wrapApiCall(this.createUserContact(contact), contact)),
      ...contactsToUpdate.map(contact => this.wrapApiCall(this.updateUserContact(contact), contact))
    ]

    return from(allOperations).pipe(
      mergeMap(operation => operation, 5),
      toArray()
    )

  }

  public async splitContacts(formValues: BeneficFormGroupDTO[][]){

    let contactsToSave: UserContact[] = [];
    let contactsToUpdate: UserContact[] = [];

    for(let formGroup of formValues){

      for(let singleForm of formGroup){
        if(singleForm.saveInContacts){
          if(singleForm.idContact){
            const contactToUpdate: UserContact = {
              idContact: singleForm.idContact,
              name: singleForm.name,
              surname: singleForm.lastname,
              email: singleForm.email,
            }

            const oldContact = await firstValueFrom(this.store.select(selectContactsById(singleForm.idContact)));

            //? skips update if contact is not modified
            if(oldContact && this.isSameContact(oldContact, contactToUpdate)){
              continue;
            }

            const alreadyExists = contactsToUpdate.some(c =>
              c.name === contactToUpdate.name &&
              c.surname === contactToUpdate.surname &&
              c.email === contactToUpdate.email
            );

            if(!alreadyExists){
              contactsToUpdate.push(contactToUpdate);
            }

          } else {
            const contactToSave: UserContact = {
              name: singleForm.name,
              surname: singleForm.lastname,
              email: singleForm.email,
            }

            const alreadyExists = contactsToSave.some(c =>
              c.name === contactToSave.name &&
              c.surname === contactToSave.surname &&
              c.email === contactToSave.email
            );

            if (!alreadyExists) {
              contactsToSave.push(contactToSave);
            }
          }
        }
      }
    }

    return {
      contactsToSave,
      contactsToUpdate
    }
  }

  private wrapApiCall(call$:Observable<{ result: IntegrationApiResult }>, contact: UserContact){
    return call$.pipe(
      map(res => ({ success: true, contact, res })),
      catchError(err => of({ success: false, contact, err }))
    )
  }

  private isSameContact(oldContact: UserContact, newContact: UserContact){
    return oldContact.name === newContact.name &&
      oldContact.surname === newContact.surname &&
      oldContact.email === newContact.email
  }

}
